import {
  Account,
  PayStatement,
  WorkLog,
  WorkLogStatus
} from "@wingspanhq/wingspan-api-sdk";
import sumBy from "lodash/sumBy";

import {
  PaystubDeductionGroup,
  PaystubDetails,
  PaystubEarningLine,
  PaystubWorkLog
} from "../types";
import {
  formatPayPeriodRange,
  formatV3Address,
  parsePayrollAmount
} from "../utils";

const EMPTY_VALUE = "—";

/**
 * Group the pay statement's payee tax withholdings into the display groups.
 * The v3 `PayStatement` no longer models separate pre-tax deductions or
 * per-state tax lines — worker withholdings arrive as a flat `payeeTaxes` list
 * (`amount` + `description`), so they render as a single "Withholdings" group
 * labelled by each entry's description. Employer contributions (`payerTaxes`)
 * are the employer's cost, not deducted from the worker, so they are not shown
 * here. Per-line YTD is 0 — only whole-statement YTD totals exist (see
 * `amountDetails.ytd`, wired at the statement level below).
 */
const selectDeductionGroups = (
  payStatement: PayStatement
): PaystubDeductionGroup[] => {
  const payeeTaxes = payStatement.payeeTaxes ?? [];
  if (!payeeTaxes.length) {
    return [];
  }

  return [
    {
      label: "Withholdings",
      lines: payeeTaxes.map(tax => ({
        label: tax.description ?? "Withholding",
        thisPeriod: parsePayrollAmount(tax.amount),
        yearToDate: 0
      }))
    }
  ];
};

const selectEarnings = (payStatement: PayStatement): PaystubEarningLine[] =>
  (payStatement.earnings ?? []).map(earning => ({
    label: earning.type ? `${earning.type}` : "Earnings",
    rate: earning.rate ?? undefined,
    hours: earning.hours ?? undefined,
    thisPeriod: parsePayrollAmount(earning.amount),
    yearToDate: 0
  }));

/** Worker display name from the account's compliance entity (legal name). */
const selectEmployeeName = (account?: Account): string => {
  const name = account?.complianceEntity?.individualLegalName;
  return (
    name?.fullLegalName ||
    [name?.givenName, name?.familyName].filter(Boolean).join(" ") ||
    account?.complianceEntity?.legalName ||
    EMPTY_VALUE
  );
};

/** Worker mailing address (falls back through the CE address fields). */
const selectEmployeeAddress = (account?: Account): string => {
  const ce = account?.complianceEntity;
  const address =
    ce?.mailingAddress ??
    ce?.address ??
    ce?.physicalAddress ??
    ce?.legalAddress;
  return formatV3Address(address) || EMPTY_VALUE;
};

type Input = {
  payStatement: PayStatement;
  /** All work logs for the pay period; filtered to this statement's sources here. */
  workLogs: WorkLog[];
  /** The payee (`payStatement.payeeAccountId`) account — supplies name + address. */
  payeeAccount?: Account;
  /** The owning payer account (`payStatement.payerAccountId`) — supplies "Created by". */
  payerAccount?: Account;
};

/**
 * Adapt a v3 `PayStatement` (+ the period's work logs and the payee/payer
 * accounts) into the paystub detail view model.
 *
 * TODO(v3): remaining gaps that fall back to `0` / `EMPTY_VALUE`: per-line
 * year-to-date figures (only whole-statement YTD totals exist, via
 * `amountDetails.ytd`) and `payoutMethodLabel` (assemblable from PayoutSettings +
 * ExternalBankAccount but gated by a payer→payee bank-data grant, PLA-3198).
 */
export const selectorPaystubDetails = ({
  payStatement,
  workLogs,
  payeeAccount,
  payerAccount
}: Input): PaystubDetails => {
  // The v3 pay statement no longer carries `sourceWorkLogIds`; each earning line
  // references the work log it paid, so the source set is the earnings' ids.
  const sourceIds = new Set(
    (payStatement.earnings ?? [])
      .map(earning => earning.workLogId)
      .filter((id): id is string => !!id)
  );

  const includedWorkLogs: PaystubWorkLog[] = workLogs
    .filter(workLog => workLog.id && sourceIds.has(workLog.id))
    .map(workLog => ({
      id: workLog.id ?? "",
      label: workLog.externalId ?? workLog.id ?? "",
      amount: parsePayrollAmount(workLog.totalAmount)
    }));

  // Submitted/approved work logs in the period that this statement didn't pay.
  const excludedWorkLogs: PaystubWorkLog[] = workLogs
    .filter(
      workLog =>
        (workLog.status === WorkLogStatus.IN_REVIEW ||
          workLog.status === WorkLogStatus.APPROVED) &&
        workLog.id &&
        !sourceIds.has(workLog.id)
    )
    .map(workLog => ({
      id: workLog.id ?? "",
      label: workLog.externalId ?? workLog.id ?? "",
      amount: parsePayrollAmount(workLog.totalAmount)
    }));

  const { amountDetails } = payStatement;
  const grossPayThisPeriod = parsePayrollAmount(amountDetails?.grossPay);
  const netPayThisPeriod = parsePayrollAmount(amountDetails?.netPay);
  const grossPayYearToDate = parsePayrollAmount(amountDetails?.ytd?.grossPay);
  const netPayYearToDate = parsePayrollAmount(amountDetails?.ytd?.netPay);

  return {
    id: payStatement.id ?? "",
    paystubNumber: payStatement.payStatementNumber ?? payStatement.id ?? "",
    createdBy:
      payerAccount?.profile?.displayName ??
      payerAccount?.complianceEntity?.legalName ??
      EMPTY_VALUE,
    payPeriodRange: formatPayPeriodRange(
      payStatement.payPeriodStartDate,
      payStatement.payPeriodEndDate
    ),
    paidDate: payStatement.payDate,
    employeeName: selectEmployeeName(payeeAccount),
    employeeAddress: selectEmployeeAddress(payeeAccount),
    earnings: selectEarnings(payStatement),
    grossPayThisPeriod,
    grossPayYearToDate,
    deductionGroups: selectDeductionGroups(payStatement),
    deductionTotalThisPeriod: Math.max(
      grossPayThisPeriod - netPayThisPeriod,
      0
    ),
    deductionTotalYearToDate: Math.max(
      grossPayYearToDate - netPayYearToDate,
      0
    ),
    netPayThisPeriod,
    netPayYearToDate,
    payoutMethodLabel: EMPTY_VALUE,
    workLogs: includedWorkLogs,
    workLogsTotal: sumBy(includedWorkLogs, workLog => workLog.amount),
    excludedWorkLogs
  };
};
